// Utility functions for parsing CSV files

export const validateCSVFormat = (csvText) => {
  const lines = csvText.trim().split('\n');

  if (lines.length < 2) {
    throw new Error('CSV file must have at least a header row and one data row');
  }

  const headers = lines[0].split(',').map(header => header.trim().toLowerCase());

  // Check if required columns exist
  if (!headers.includes('postcode')) {
    throw new Error('CSV file must have a "postcode" column');
  }

  if (!headers.includes('quantity')) {
    throw new Error('CSV file must have a "quantity" column');
  }

  // Check if there are exactly 2 columns
  if (headers.length !== 2) {
    throw new Error('CSV file must have exactly 2 columns: "postcode" and "quantity"');
  }

  return true;
};

export const parseCSV = (csvText) => {
  // Validate format first
  validateCSVFormat(csvText);

  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',').map(header => header.trim());

  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(value => value.trim());
    if (values.length === headers.length && values[0] && values[1]) {
      const row = {};
      headers.forEach((header, index) => {
        // Convert postcode to string and quantity to number
        if (header.toLowerCase() === 'postcode') {
          row[header] = values[index];
        } else if (header.toLowerCase() === 'quantity') {
          const quantity = parseInt(values[index], 10);
          if (isNaN(quantity) || quantity < 0) {
            throw new Error(`Invalid quantity "${values[index]}" on row ${i + 1}. Quantity must be a positive number.`);
          }
          row[header] = quantity;
        } else {
          row[header] = values[index];
        }
      });
      data.push(row);
    }
  }

  if (data.length === 0) {
    throw new Error('No valid data rows found in CSV file');
  }

  return data;
};

export const csvToOrdersCounts = (csvData) => {
  const ordersCounts = {};

  csvData.forEach(row => {
    const postcode = row.postcode || row.Postcode;
    const quantity = row.quantity || row.Quantity;

    if (postcode && quantity) {
      ordersCounts[postcode] = quantity;
    }
  });

  return ordersCounts;
};

export const readCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const parsedData = parseCSV(csvText);
        const ordersCounts = csvToOrdersCounts(parsedData);
        resolve(ordersCounts);
      } catch (error) {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsText(file);
  });
};

export const loadPredefinedCSV = async (csvName) => {
  try {
    // Try to fetch the CSV file from the public folder
    const response = await fetch(`/csvData/${csvName}.csv`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const csvText = await response.text();
    const parsedData = parseCSV(csvText);
    const ordersCounts = csvToOrdersCounts(parsedData);
    return ordersCounts;
  } catch (error) {
    throw new Error(`Error loading predefined CSV "${csvName}": ${error.message}`);
  }
};